import { useMemo } from 'react';

import { useAuth } from '@/utils/auth/context';
import { isSecurityDisabled } from '@/utils/devModeConfig';

import { usePermissionOverride } from './dev/usePermissionOverride';
import { Permission } from './permissions';
import { resolveAllPermissions, resolvePermissions } from './resolvePermissions';

export type BamAuthApi = {
  isLoggedIn: boolean;
  permissions: Set<Permission>;
  hasPermission: (permission: Permission, featureToggle?: boolean) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  isImpersonating: boolean;
};

/**
 * Central hook for permission checks across the platform.
 *
 * Resolves backend JWT roles into fine-grained frontend permissions.
 * In dev mode, supports permission override via the Permission Switcher.
 */
export function useBamAuth(): BamAuthApi {
  const auth = useAuth();
  const override = usePermissionOverride();

  const isLoggedIn = isSecurityDisabled() || !!auth?.isAuthenticated;

  const permissions = useMemo<Set<Permission>>(() => {
    // Dev impersonation takes highest priority
    if (override.isActive) {
      return override.permissions;
    }

    // Security disabled = all permissions granted (dev/test)
    if (isSecurityDisabled()) {
      return resolveAllPermissions();
    }

    // Normal flow: resolve from JWT roles
    const roles = auth?.user?.allRolesForUser ?? [];
    return resolvePermissions(roles);
  }, [override.isActive, override.permissions, auth?.user?.allRolesForUser]);

  return useMemo(
    () => ({
      isLoggedIn,
      permissions,
      hasPermission: (permission: Permission, featureToggle: boolean = true) =>
        permissions.has(permission) && featureToggle,
      hasAnyPermission: (ps: Permission[]) => ps.some((p) => permissions.has(p)),
      hasAllPermissions: (ps: Permission[]) => ps.every((p) => permissions.has(p)),
      isImpersonating: override.isActive,
    }),
    [isLoggedIn, permissions, override.isActive],
  );
}
